/**
 * useServiceOrders - Cadastro da Ordem de Serviço (Fase 4)
 *
 * Criação, edição, leitura para edição e checagem de OS duplicada (BR-OS-002),
 * 100% via RPC. O cliente `supabase` é preservado com `.call` (RPCs fora da tipagem).
 *
 * RELEASE: 2026-10-11 v1.4.0 - Lista de O.S. sem polling de 30s; relê ao abrir/focar e via tempo real.
 * RELEASE: 2026-10-09 v1.3.0 - Documentos e contador prospectivo de agendamentos na lista.
 * RELEASE: 2026-10-09 v1.2.0 - Cronômetro, prazo e filtros equivalentes na lista e Kanban.
 * RELEASE: 2026-10-08 v1.1.0 - Fase 5: listagem, Kanban e mover de fase.
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { SERVICE_ORDER_KANBAN_PAGE_SIZE } from '@/lib/serviceOrderKanbanRules';
import type { ServiceOrderDeadlineBucket } from '@/lib/serviceOrderTiming';
import type { ServiceOrderJourney } from '@/lib/serviceOrderJourney';

/** Logística (2026-10-16): awaiting_shipment/shipment_in_transit/shipment_delivered são definidas pela
 * remessa vinculada (gatilho no servidor); to_reschedule = agendamento desmarcado. */
export type ServiceOrderStatus = 'open' | 'scheduled' | 'in_progress' | 'awaiting_review' | 'in_correction' | 'completed' | 'cancelled'
  | 'awaiting_shipment' | 'shipment_in_transit' | 'shipment_delivered' | 'to_reschedule';

/** Situações que podem ser agendadas (espelha reschedule_service_order v1.1.0). */
export const SERVICE_ORDER_SCHEDULABLE: readonly ServiceOrderStatus[] = ['open', 'scheduled', 'shipment_delivered', 'to_reschedule'];
/** Situações sem agendamento que podem ser iniciadas (espelha change_service_order_status v1.11.0). */
export const SERVICE_ORDER_STARTABLE: readonly ServiceOrderStatus[] = ['open', 'scheduled', 'shipment_delivered', 'to_reschedule'];
/** Remessa só pode ser criada/vinculada com a O.S. Aberta (espelha set_shipment_link v1.3.0). */
export const canLinkShipmentToServiceOrder = (status: string | null | undefined) => status === 'open';
export type ServiceOrderPriority = 'low' | 'medium' | 'high' | 'urgent';

export const SERVICE_ORDER_STATUS_LABEL: Record<ServiceOrderStatus, string> = {
  open: 'Aberta',
  scheduled: 'Agendada',
  in_progress: 'Em andamento',
  awaiting_review: 'Aguardando revisão',
  // BR-OS-034: OS reaberta/devolvida, isolada da primeira execução
  in_correction: 'Em correção',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  awaiting_shipment: 'Aguardando postagem',
  shipment_in_transit: 'Em trânsito',
  shipment_delivered: 'Entregue',
  to_reschedule: 'Reagendar',
};

export const SERVICE_ORDER_PRIORITIES: { value: ServiceOrderPriority; label: string }[] = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export interface ServiceOrderAddress {
  cep: string; street: string; number: string; complement: string; district: string; city: string; state: string;
  /** Ponto de referência, telefone e nome do contato no local e link do Google Maps (opcionais). */
  reference: string; phone: string; contact_name: string; maps_url: string;
}

export interface ServiceOrderInput {
  id?: string | null;
  service_type_id: string | null;
  category_id: string | null;
  phase_id: string | null;
  priority: ServiceOrderPriority;
  provider_person_id: string | null;
  customer_id: string | null;
  vehicle_id: string | null;
  /** Usuário interno; não é o prestador de serviço. */
  responsible_user_id?: string | null;
  address: ServiceOrderAddress;
  /** Data/hora local no formato do input datetime-local ('' = sem agendamento). */
  scheduled_start: string;
  description: string;
  /** BR-OS-017: aprovação de Instalação com kit já instalado (somente na criação). */
  install_override?: boolean;
  override_reason?: string;
  /** BR-OS-020: vendedor (obrigatório quando o tipo exige). */
  seller_id?: string | null;
  /** Coordenadas do local coladas do Google Maps ("lat, lng"); '' limpa. */
  coordinates?: string;
}

export interface ServiceOrderEditData extends Omit<ServiceOrderInput, 'scheduled_start'> {
  id: string;
  number: string;
  status: ServiceOrderStatus;
  provider_name: string | null;
  customer_name: string | null;
  vehicle_label: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  /** Coordenadas do local (gravadas na OS; exibidas como "lat, lng" no formulário). */
  destination_lat: number | null;
  destination_lng: number | null;
}

export interface ServiceOrderDuplicate { id: string; number: string; status: ServiceOrderStatus; service_type_name: string }

/** BR-OS-017: kit ativo instalado no veículo. */
export interface ServiceOrderInstalledKit { id: string; label: string; tracker: string | null; chip: string | null; since: string }

/** Retorno de check_service_order_duplicate v1.1.0 (BR-OS-002 v2 + BR-OS-017). */
export interface ServiceOrderCreationGuard {
  duplicate: ServiceOrderDuplicate | null;
  installed_kits: ServiceOrderInstalledKit[];
  stock_movement: string | null;
  uninstall_without_kit: boolean;
  requires_install_approval: boolean;
  can_approve_install: boolean;
}

const rpc = (name: string, args: Record<string, unknown>) => (supabase.rpc as any).call(supabase, name, args);

/** Converte o valor local do input para ISO (com fuso do navegador). */
const toIso = (local: string) => (local ? new Date(local).toISOString() : null);

export function useServiceOrderEditData(id: string | null) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['service-order-edit', currentTenant?.id, id],
    enabled: !!currentTenant?.id && !!id,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_edit_data', { p_id: id, p_tenant_hint: currentTenant!.id });
      if (error) throw error;
      return data as ServiceOrderEditData;
    },
  });
}

/** BR-OS-002 v2 / BR-OS-017: OS aberta do veículo (qualquer tipo) e kit instalado x tipo. */
export function useServiceOrderDuplicate(vehicleId: string | null, serviceTypeId: string | null, excludeId: string | null) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['service-order-duplicate', currentTenant?.id, vehicleId, serviceTypeId, excludeId],
    enabled: !!currentTenant?.id && !!vehicleId,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await rpc('check_service_order_duplicate', {
        p_vehicle_id: vehicleId, p_service_type_id: serviceTypeId, p_exclude_id: excludeId, p_tenant_hint: currentTenant!.id,
      });
      if (error) throw error;
      return (data ?? null) as ServiceOrderCreationGuard | null;
    },
  });
}

export function useSaveServiceOrder() {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ServiceOrderInput) => {
      const args = {
        p_service_type_id: input.service_type_id,
        p_category_id: input.category_id,
        p_phase_id: input.phase_id,
        p_priority: input.priority,
        p_provider_person_id: input.provider_person_id,
        p_customer_id: input.customer_id,
        p_vehicle_id: input.vehicle_id,
        p_responsible_user_id: input.responsible_user_id ?? null,
        p_address: input.address,
        p_scheduled_start: toIso(input.scheduled_start),
        p_description: input.description,
        // BR-OS-020: vendedor da OS
        p_seller_id: input.seller_id ?? null,
        // Coordenadas do local (texto "lat, lng"; validação definitiva no servidor)
        p_coordinates: input.coordinates ?? null,
        p_tenant_hint: currentTenant?.id ?? null,
      };
      const { data, error } = input.id
        ? await rpc('update_service_order', { p_id: input.id, ...args })
        : await rpc('create_service_order', {
            ...args,
            p_install_override: !!input.install_override,
            p_override_reason: input.override_reason?.trim().slice(0, 500) || null,
          });
      if (error) throw error;
      return data as { id: string; number: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['service-order-edit'] });
      // Reabrir os detalhes deve refletir imediatamente o responsável recém-salvo.
      qc.invalidateQueries({ queryKey: ['service-order-details'] });
      qc.invalidateQueries({ queryKey: ['service-order-duplicate'] });
      qc.invalidateQueries({ queryKey: ['service-order-phase-permissions'] });
    },
  });
}

/** Fim previsto = início + duração do tipo (mesma regra do servidor). */
export function computeScheduledEnd(startLocal: string, durationMinutes: number | null | undefined): Date | null {
  if (!startLocal || !durationMinutes) return null;
  return new Date(new Date(startLocal).getTime() + durationMinutes * 60_000);
}

// ============================================================================
// Fase 5 — Listagem e Kanban
// RELEASE: 2026-10-08 v1.1.0 - Listagem paginada, colunas do Kanban por cursor,
//          contagens por fase e mover de fase. Atualização a cada 30 s (sem Realtime:
//          a tabela não é lida diretamente pelo navegador).
// ============================================================================

export interface ServiceOrderRow {
  id: string; number: string; status: ServiceOrderStatus; priority: ServiceOrderPriority;
  service_type_id: string; service_type_name: string; service_type_color: string;
  category_id: string; category_name: string; category_color: string;
  phase_id: string; phase_name: string; phase_color: string; phase_sort_order: number; total_category_phases: number;
  provider_person_id: string; provider_name: string; provider_document: string | null;
  customer_id: string; customer_name: string | null; customer_document: string | null;
  vehicle_id: string | null; vehicle_plate: string | null; vehicle_label: string | null;
  responsible_user_id: string | null; responsible_name: string | null; responsible_avatar_url: string | null;
  /** Marcos mínimos da jornada oficial, sem dados privados de chegada. */
  journey?: ServiceOrderJourney | null;
  address_city: string | null; address_state: string | null;
  scheduled_start: string | null; scheduled_end: string | null; is_overdue: boolean;
  started_at: string | null; reviewed_at: string | null; completed_at: string | null; cancelled_at: string | null;
  active_seconds: number; deadline_seconds: number; is_paused: boolean; current_pause_started_at: string | null;
  schedule_count: number;
  created_at: string; updated_at: string;
}

export type ServiceOrderSortField = 'number' | 'status' | 'scheduled_start' | 'service_type' | 'provider' | 'customer' | 'created_at' | 'completed_at' | 'timer' | 'deadline';
export type ServiceOrderDateField = 'created_at' | 'scheduled_start' | 'completed_at';

export interface ServiceOrdersFilters {
  search: string;
  // status null = sem filtro (nenhum card KPI selecionado, igual à listagem de Tarefas)
  status: ServiceOrderStatus | 'overdue' | null;
  serviceTypeId: string | null;
  providerId: string | null;
  categoryId: string | null;
  phaseId: string | null;
  deadlineBucket: ServiceOrderDeadlineBucket | null;
  dateField: ServiceOrderDateField;
  dateFrom: string | null;
  dateTo: string | null;
  sortField: ServiceOrderSortField;
  sortDirection: 'asc' | 'desc';
  page: number;
  pageSize: number;
  /** Filtro fixo da aba O.S do modal de Veículo (validado no servidor) */
  vehicleId?: string | null;
  /** Filtro fixo da aba O.S do modal de Cliente (validado no servidor) */
  customerId?: string | null;
  /** v1.6.0: filtro fixo por lista de O.S. (aba Vínculos da remessa) */
  ids?: string[] | null;
  /** Filtro fixo da aba Ordens de serviço do modal do Negócio (validado no servidor) */
  dealId?: string | null;
}

export interface ServiceOrderKpis {
  total: number; open: number; scheduled: number; in_progress: number; awaiting_review: number; in_correction?: number;
  awaiting_shipment?: number; shipment_in_transit?: number; shipment_delivered?: number; to_reschedule?: number;
  completed: number; cancelled: number; overdue: number;
}

const REFRESH_MS = 30_000;
const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';

export function useServiceOrdersList(f: ServiceOrdersFilters, enabled = true) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['service-orders', 'list', currentTenant?.id, f],
    // `enabled` opcional: abas de modal só consultam com módulo/permissão ativos
    enabled: !!currentTenant?.id && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true, // v1.4.0: sem polling; tempo real + foco
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_orders_paginated', {
        p_search: f.search || null,
        p_status: f.status ?? null,
        p_service_type_id: f.serviceTypeId,
        p_provider_person_id: f.providerId,
        p_category_id: f.categoryId,
        p_phase_id: f.phaseId,
        p_deadline_bucket: f.deadlineBucket,
        p_date_field: f.dateField,
        p_date_from: f.dateFrom,
        p_date_to: f.dateTo,
        p_timezone: tz(),
        p_sort_field: f.sortField,
        p_sort_direction: f.sortDirection,
        p_page: f.page,
        p_page_size: f.pageSize,
        p_vehicle_id: f.vehicleId ?? null,
        p_customer_id: f.customerId ?? null,
        p_ids: f.ids?.length ? f.ids.slice(0, 5000) : null,
        p_deal_id: f.dealId ?? null,
        p_tenant_hint: currentTenant!.id,
      });
      if (error) throw error;
      return data as { items: ServiceOrderRow[]; total: number; kpis: ServiceOrderKpis };
    },
  });
}

export interface ServiceOrderKanbanFilters {
  search: string;
  serviceTypeId: string | null;
  providerId: string | null;
  categoryId: string | null;
  deadlineBucket: ServiceOrderDeadlineBucket | null;
  dateField: ServiceOrderDateField;
  dateFrom: string | null;
  dateTo: string | null;
}

export function useServiceOrderKanbanColumn(phaseId: string, f: ServiceOrderKanbanFilters) {
  const { currentTenant, user } = useAuth();
  return useInfiniteQuery({
    queryKey: ['service-orders', 'kanban-column', currentTenant?.id, user?.id, phaseId, f],
    enabled: !!currentTenant?.id && !!phaseId,
    refetchInterval: REFRESH_MS,
    initialPageParam: null as null | { created_at: string; id: string },
    queryFn: async ({ pageParam }) => {
      const { data, error } = await rpc('get_service_order_kanban_column', {
        p_phase_id: phaseId,
        p_cursor_created_at: pageParam?.created_at ?? null,
        p_cursor_id: pageParam?.id ?? null,
        p_limit: SERVICE_ORDER_KANBAN_PAGE_SIZE,
        p_search: f.search || null,
        p_service_type_id: f.serviceTypeId,
        p_provider_person_id: f.providerId,
        p_category_id: f.categoryId,
        p_deadline_bucket: f.deadlineBucket,
        p_date_field: f.dateField,
        p_date_from: f.dateFrom,
        p_date_to: f.dateTo,
        p_timezone: tz(),
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
      return data as { items: ServiceOrderRow[]; has_more: boolean };
    },
    getNextPageParam: (last) => {
      const tail = last.items[last.items.length - 1];
      return last.has_more && tail ? { created_at: tail.created_at, id: tail.id } : undefined;
    },
  });
}

/** Conta somente fases da categoria ativa; abas usam exclusivamente o catálogo. */
export function useServiceOrderKanbanCounts(categoryId: string | null, f: ServiceOrderKanbanFilters) {
  const { currentTenant, user } = useAuth();
  return useQuery({
    queryKey: ['service-orders', 'kanban-counts', currentTenant?.id, user?.id, categoryId, f],
    enabled: !!currentTenant?.id && !!categoryId,
    refetchInterval: REFRESH_MS,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_kanban_counts', {
        p_category_id: categoryId, p_search: f.search || null, p_service_type_id: f.serviceTypeId,
        p_provider_person_id: f.providerId, p_date_from: f.dateFrom, p_date_to: f.dateTo,
        p_date_field: f.dateField, p_deadline_bucket: f.deadlineBucket, p_timezone: tz(),
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
      const map: Record<string, { total: number; overdue: number }> = {};
      for (const r of (data ?? []) as { phase_id?: string; total: number; overdue: number }[]) {
        const id = r.phase_id;
        if (id) map[id] = r;
      }
      return map;
    },
  });
}

export function useMoveServiceOrderPhase() {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    // BR-OS-021: categoryId opcional permite trocar de categoria junto com a fase
    // v1.2.0 da RPC: note opcional é anexada ao evento de troca de fase na timeline
    mutationFn: async ({ id, phaseId, categoryId, note }: { id: string; phaseId: string; categoryId?: string | null; note?: string | null }) => {
      const { data, error } = await rpc('move_service_order_phase', { p_id: id, p_phase_id: phaseId, p_category_id: categoryId ?? null, p_note: note ?? null, p_tenant_hint: currentTenant?.id ?? null });
      if (error) throw error;
      return data as ServiceOrderRow;
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['service-order-details'] });
      qc.invalidateQueries({ queryKey: ['service-order-timeline'] });
    },
  });
}

/** Status final não pode ser arrastado (mesma regra do servidor). */
export const isServiceOrderLocked = (status: ServiceOrderStatus) => status === 'completed' || status === 'cancelled';
